import { aye, nay } from '@/helpers'
import { createEvent, createStore, sample } from 'effector'
import { register } from '~/core/repository'
import {
  model as popupModel,
  type IPinLockPopupProps,
} from '~/entities/info-popup'
import {
  type TvChannelUrlLibraryParams,
  type TvChannelUrlSpecialParams,
} from '~/entities/redirect-url'
import { model as tvCategoriesModel } from '~/entities/tv/categories'
import {
  model as genericPaymentModel,
  model as paymentModel,
} from '~/features/payment/generic'
import { type TvChannelPayment } from '~/features/payment/interfaces'
import {
  siginInToWatchFeatureModel,
  type SignInToWatchItem,
} from '~/features/signin-to-watch'
import { model as guestModel } from '~/features/subscriber/guest'
import '~/shared/ui/translations/general-translations'
import { formatSelectedTvChannel } from './helpers'

export const selectActiveChannel = createEvent<
  TvChannelPayment &
    IPinLockPopupProps &
    TvChannelUrlLibraryParams &
    TvChannelUrlSpecialParams &
    SignInToWatchItem
>()

const defaultSelectChannelState: TvChannelPayment & SignInToWatchItem = {
  id: 0,
  channelNumber: 0,
  categoryId: 0,
  name: '',
  logoUrl: '',
  purchaseInfo: null,
  free: true,
  priceSettings: null,
  redirect: '',
}
export const $selectedChannel = createStore<
  TvChannelPayment & SignInToWatchItem
>(defaultSelectChannelState).on(selectActiveChannel, (_, payload) => payload)

sample({
  clock: selectActiveChannel,
  source: {
    selectedChannel: $selectedChannel,
    unlockedId: popupModel.$unlockedId,
  },
  filter: ({ selectedChannel, unlockedId }) =>
    aye(selectedChannel.locked) ||
    (Boolean(unlockedId) && selectedChannel.id !== unlockedId),
  fn: (_, clockData) => clockData,
  target: popupModel.openPinLockPopup,
})

// clear pinlock session storage on close payment popup
sample({
  clock: genericPaymentModel.tryClosePaymentPopup,
  source: {
    selectedChannel: $selectedChannel,
  },
  filter: ({ selectedChannel }, _) => Boolean(selectedChannel.id),
  target: popupModel.resetUnlockId,
})

const selectTvChannel = createEvent<
  TvChannelPayment & TvChannelUrlLibraryParams & TvChannelUrlSpecialParams
>()

// TODO during the refractor, it is necessary to transfer to featured or widgets if you transfer now, we get a cyclic dependence
sample({
  clock: selectActiveChannel,
  source: {
    selectedChannel: $selectedChannel,
    unlockedId: popupModel.$unlockedId,
    categories: tvCategoriesModel.$tvCategoriesWithChannels,
  },
  filter: ({ selectedChannel, unlockedId }) =>
    nay(selectedChannel.locked) || selectedChannel.id === unlockedId,
  fn: ({ selectedChannel, categories }) =>
    formatSelectedTvChannel({ selectedChannel, categories }),
  target: selectTvChannel,
})

sample({
  clock: popupModel.$unlockedId,
  source: {
    selectedChannel: $selectedChannel,
    unlockChannelId: popupModel.$unlockedId,
  },
  filter: ({ selectedChannel, unlockChannelId }, value) =>
    aye(value) &&
    selectedChannel.id === unlockChannelId &&
    nay(selectedChannel.isBanner),
  fn: ({ selectedChannel }) => selectedChannel,
  target: selectTvChannel,
})

sample({
  clock: selectTvChannel,
  source: {
    guest: guestModel.$guest,
    selectedChannel: $selectedChannel,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && !free && !purchaseInfo,
  fn: ({ selectedChannel }) => ({
    name: selectedChannel.name,
    redirect: selectedChannel.redirect,
  }),
  target: siginInToWatchFeatureModel.openSignInToWatchPopup,
})

sample({
  clock: selectTvChannel,
  source: {
    guest: guestModel.$guest,
    selectedChannel: $selectedChannel,
  },
  filter: ({ guest }, { free, purchaseInfo }) =>
    aye(guest) && (aye(free) || aye(purchaseInfo)),
  fn: (_, tvChannel) => tvChannel,
  target: paymentModel.selectTvChannel,
})

sample({
  clock: selectTvChannel,
  source: guestModel.$guest,
  filter: nay,
  fn: (_, tvChannel) => tvChannel,
  target: paymentModel.selectTvChannel,
})

register(selectActiveChannel, 'tvActiveChannelWidget/selectActiveChannel')
